import type { Job, Enterprise, Policy, Notice, JobFair, School } from '@/types';
import { formatDateTime, htmlToPlainText } from '@/lib/utils';

function safeParseArray(value: unknown): string[] {
  const parsed = parseArrayValue(value);
  const seen = new Set<string>();
  return parsed
    .map(item => String(item || '').replace(/^["'\[\]\s]+|["'\[\]\s]+$/g, '').trim())
    .filter(Boolean)
    .filter(item => {
      if (item === '[object Object]' || item === 'object Object' || item === 'null' || item === 'undefined') return false;
      if (seen.has(item)) return false;
      seen.add(item);
      return true;
    });
}

export function normalizeGenderText(value: unknown): '男' | '女' {
  const text = safeParseString(value).trim().toLowerCase();
  if (['男', 'male', 'm', '1', 'man', 'boy'].includes(text)) return '男';
  if (['女', 'female', 'f', '2', 'woman', 'girl'].includes(text)) return '女';
  return '男';
}

export function mapTalent(raw: Record<string, unknown>): Record<string, unknown> {
  return {
    ...raw,
    id: String(raw.id ?? ''),
    gender: normalizeGenderText(raw.gender),
  };
}

function parseArrayValue(value: unknown, depth = 0): string[] {
  if (depth > 5 || value === null || value === undefined || value === false) return [];
  if (Array.isArray(value)) return value.flatMap(item => parseArrayValue(item, depth + 1));
  if (typeof value === 'object') {
    const data = value as Record<string, unknown>;
    for (const key of ['url', 'src', 'path', 'href', 'image', 'image_url', 'imageUrl', 'label', 'name', 'title', 'text', 'value', 'benefit', 'welfare', 'tag', 'skill', 'major']) {
      const values = parseArrayValue(data[key], depth + 1);
      if (values.length) return values;
    }
    return ['images', 'photos', 'files', 'attachments', 'benefits', 'benefitsList', 'welfares', 'welfareList', 'tags', 'items', 'list', 'data']
      .flatMap(key => parseArrayValue(data[key], depth + 1));
  }
  if (typeof value === 'number') return [String(value)];
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return [];
    if (/^[\[{"']/.test(text)) {
      try {
        const decoded = JSON.parse(text);
        if (decoded !== value) return parseArrayValue(decoded, depth + 1);
      } catch {
        // 按普通分隔文本兜底
      }
    }
    return text.split(/[,，、;；|\n\r\t]+|\s{2,}/).map(item => item.trim()).filter(Boolean);
  }
  return [];
}

function normalizeAssetURL(value: unknown): string {
  const text = safeParseString(value).trim();
  if (!text || text === '[object Object]' || text === 'null' || text === 'undefined') return '';
  if (/^(https?:)?\/\//i.test(text)) {
    return text.startsWith('//') ? `https:${text}` : text;
  }
  if (/^(data|blob):/i.test(text)) return text;
  if (text.startsWith('/')) return text;

  const qiniuDomain = String(import.meta.env.VITE_QINIU_DOMAIN || 'https://cdn.your-domain.com').trim().replace(/\/+$/, '');
  if (qiniuDomain) {
    const normalizedDomain = /^https?:\/\//i.test(qiniuDomain) ? qiniuDomain : `https://${qiniuDomain}`;
    return `${normalizedDomain}/${text.replace(/^\/+/, '')}`;
  }
  return `/${text.replace(/^\/+/, '')}`;
}

function safeParseAssetArray(...values: unknown[]): string[] {
  const seen = new Set<string>();
  return values
    .flatMap(value => parseArrayValue(value))
    .map(normalizeAssetURL)
    .filter(Boolean)
    .filter(item => {
      if (seen.has(item)) return false;
      seen.add(item);
      return true;
    });
}

const ENTERPRISE_NON_GALLERY_RE = /(公章|营业执照|执照|许可证|资质|认证|授权|证明|证件|身份证|合同|协议|发票|实地验证|核验|就业凭证|材料)/;
const ENTERPRISE_GALLERY_RE = /(实拍|环境|办公|厂区|园区|车间|门店|前台|形象|展示|照片|图片|场景)/;

function parseEnterpriseGalleryAssetArray(value: unknown, depth = 0): string[] {
  if (depth > 5 || value === null || value === undefined || value === false) return [];
  if (Array.isArray(value)) return value.flatMap(item => parseEnterpriseGalleryAssetArray(item, depth + 1));
  if (typeof value === 'object') {
    const data = value as Record<string, unknown>;
    const label = [
      data.category,
      data.type,
      data.name,
      data.title,
      data.label,
      data.remark,
    ].map(safeParseString).join(' ');
    if (ENTERPRISE_NON_GALLERY_RE.test(label) && !ENTERPRISE_GALLERY_RE.test(label)) {
      return [];
    }
    const direct = ['url', 'src', 'path', 'href', 'image', 'image_url', 'imageUrl']
      .flatMap(key => parseEnterpriseGalleryAssetArray(data[key], depth + 1));
    if (direct.length) return direct;
    return ['images', 'photos', 'files', 'attachments', 'items', 'list', 'data']
      .flatMap(key => parseEnterpriseGalleryAssetArray(data[key], depth + 1));
  }
  if (typeof value === 'number') return [String(value)];
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return [];
    if (/^[\[{"']/.test(text)) {
      try {
        const decoded = JSON.parse(text);
        if (decoded !== value) return parseEnterpriseGalleryAssetArray(decoded, depth + 1);
      } catch {
        // 按普通资源地址兜底
      }
    }
    return text.split(/[,，、;；|\n\r\t]+|\s{2,}/).map(item => item.trim()).filter(Boolean);
  }
  return [];
}

function safeParseEnterpriseGalleryAssets(...values: unknown[]): string[] {
  const seen = new Set<string>();
  return values
    .flatMap(value => parseEnterpriseGalleryAssetArray(value))
    .map(normalizeAssetURL)
    .filter(Boolean)
    .filter(item => {
      if (seen.has(item)) return false;
      seen.add(item);
      return true;
    });
}

function safeParseString(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';
  return String(value);
}

function safeParseNumber(value: unknown): number {
  if (value === null || value === undefined || value === '') return 0;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function toPlainText(value: unknown, maxLength?: number): string {
  return htmlToPlainText(safeParseString(value), maxLength);
}

function numberOrUndefined(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : undefined;
}

function formatSalaryRange(raw: Record<string, unknown>): string {
  const text = safeParseString(raw.salary_range || raw.salary);
  if (text && text !== '0-0') return text;
  const min = numberOrUndefined(raw.salary_min);
  const max = numberOrUndefined(raw.salary_max);
  if (min && max) return `${min}-${max}`;
  if (min) return `${min}以上`;
  if (max) return `${max}以下`;
  return '未设置';
}

export function mapJob(raw: Record<string, unknown>): Job & Record<string, unknown> {
  const benefits = safeParseArray(raw.benefits || raw.benefits_list || raw.other_benefits || raw.otherBenefits || raw.welfare_tags);
  const requirements = safeParseArray(raw.requirements);
  const rawType = typeof raw.type === 'string' ? raw.type : '';
  const mappedType: Job['type'] = rawType === 'batch' ? 'batch' : rawType === 'part_time' ? '兼职' : rawType === 'intern' ? '实习' : '全职';

  return {
    ...raw, // 保留原始后端字段，兼容现有代码
    id: String(raw.id ?? ''),
    title: toPlainText(raw.title),
    company: safeParseString(raw.enterprise_name || raw.company),
    enterpriseId: raw.enterprise_id || raw.enterpriseId ? String(raw.enterprise_id || raw.enterpriseId) : '',
    companyLogo: normalizeAssetURL(raw.enterprise_logo || raw.companyLogo) || undefined,
    location: safeParseString(raw.location),
    salary: formatSalaryRange(raw),
    experience: safeParseString(raw.experience),
    education: safeParseString(raw.education),
    tags: [safeParseString(raw.location), safeParseString(raw.education)].filter(Boolean),
    type: mappedType,
    category: safeParseString(raw.category || raw.industry),
    description: safeParseString(raw.description),
    requirements: requirements.length > 0 ? requirements : [safeParseString(raw.requirements)],
    benefits: benefits.length > 0 ? benefits : [safeParseString(raw.benefits)],
    publishDate: formatDateTime(safeParseString(raw.publish_date || raw.created_at)),
    headcount: Number(raw.headcount ?? 0),
    companySize: safeParseString(raw.enterprise_size || raw.companySize),
    companyIndustry: safeParseString(raw.enterprise_industry || raw.companyIndustry),
    auditStatus: (raw.audit_status as Job['auditStatus']) || (raw.auditStatus as Job['auditStatus']) || undefined,
  };
}

export function mapEnterprise(raw: Record<string, unknown>): Enterprise & Record<string, unknown> {
  const logo = normalizeAssetURL(raw.logo);
  const coverImage = normalizeAssetURL(raw.cover_image || raw.coverImage);
  const images = safeParseEnterpriseGalleryAssets(
    raw.images,
    raw.photos,
    raw.environment_images || raw.environmentImages,
    raw.scene_images || raw.sceneImages,
  );
  const siteVerifyImages = safeParseAssetArray(raw.site_verify_images || raw.siteVerifyImages || raw.verify_images || raw.verifyImages);

  return {
    ...raw, // 保留原始后端字段，兼容现有代码
    id: String(raw.id ?? ''),
    name: safeParseString(raw.name),
    logo: logo || undefined,
    industry: safeParseString(raw.industry),
    size: safeParseString(raw.size || raw.scale),
    location: safeParseString(raw.location),
    description: safeParseString(raw.description),
    jobCount: Number(raw.job_count ?? raw.jobCount ?? 0),
    tags: safeParseArray(raw.tags),
    founded: raw.founded_year ? String(raw.founded_year) : (raw.founded as string),
    website: safeParseString(raw.website),
    benefits: safeParseArray(raw.benefits),
    images,
    coverImage,
    siteVerifyImages,
    site_verify_images: siteVerifyImages,
    video: normalizeAssetURL(raw.video || raw.video_url || raw.videoUrl || raw.intro_video || raw.introVideo),
    isFamous: !!raw.is_famous,
    isHighSalary: !!raw.is_high_salary,
    scale: safeParseString(raw.scale),
    employeeCount: safeParseNumber(raw.employee_count ?? raw.employeeCount),
    employee_count: safeParseNumber(raw.employee_count ?? raw.employeeCount),
    address: safeParseString(raw.address),
  };
}

export function mapSchool(raw: Record<string, unknown>): School & Record<string, unknown> {
  const logo = normalizeAssetURL(raw.logo || raw.school_logo || raw.schoolLogo);
  const coverImage = normalizeAssetURL(raw.cover_image || raw.coverImage || raw.banner || raw.banner_image);
  const video = normalizeAssetURL(raw.video || raw.video_url || raw.videoUrl || raw.intro_video || raw.introVideo);
  const siteVerifyImages = safeParseAssetArray(raw.site_verify_images || raw.siteVerifyImages || raw.verify_images || raw.verifyImages);
  const images = safeParseAssetArray(
    raw.images,
    raw.photos,
    raw.campus_images || raw.campusImages,
  );

  return {
    ...raw,
    id: String(raw.id ?? ''),
    name: safeParseString(raw.name || raw.school_name || raw.schoolName),
    address: safeParseString(raw.address || raw.location),
    studentCount: safeParseNumber(raw.student_count ?? raw.studentCount),
    majors: safeParseArray(raw.majors || raw.major_list || raw.majorList),
    description: safeParseString(raw.description || raw.introduction || raw.intro),
    contactName: safeParseString(raw.contact_name || raw.contactName || raw.contact_person || raw.contactPerson),
    contactPhone: safeParseString(raw.contact_phone || raw.contactPhone || raw.phone),
    founded: raw.founded_year ? String(raw.founded_year) : safeParseString(raw.founded || raw.foundedYear),
    website: safeParseString(raw.website),
    employmentRate: safeParseString(raw.employment_rate ?? raw.employmentRate),
    features: safeParseArray(raw.features || raw.feature_list || raw.featureList),
    schoolType: safeParseString(raw.school_type || raw.schoolType),
    school_type: safeParseString(raw.school_type || raw.schoolType),
    logo,
    coverImage,
    cover_image: coverImage,
    images,
    video,
    siteVerifyImages,
    site_verify_images: siteVerifyImages,
  };
}

export function mapPolicy(raw: Record<string, unknown>): Policy & Record<string, unknown> {
  const userTypeCode = safeParseString(raw.user_type || raw.userType);
  const summary = toPlainText(raw.summary) || toPlainText(raw.content, 200);
  const coverImage = normalizeAssetURL(raw.cover_image || raw.coverImage || raw.image);
  return {
    ...raw, // 保留原始后端字段，兼容现有代码
    id: String(raw.id ?? ''),
    title: toPlainText(raw.title),
    categoryId: raw.category_id || raw.categoryId ? String(raw.category_id || raw.categoryId) : undefined,
    userType: (userTypeCode === 'enterprise' ? '企业' : '个人') as Policy['userType'],
    userTypeCode,
    identityType: safeParseString(raw.identity_type),
    policyType: safeParseString(raw.policy_type),
    publishDate: safeParseString(raw.publish_date || raw.created_at),
    source: safeParseString(raw.source),
    summary,
    content: safeParseString(raw.content),
    coverImage: coverImage || undefined,
    image: coverImage || undefined,
    viewCount: Number(raw.view_count ?? raw.viewCount ?? 0),
    isTop: Boolean(raw.is_top ?? raw.isTop),
  };
}

export function mapNotice(raw: Record<string, unknown>): Notice & Record<string, unknown> {
  return {
    ...raw, // 保留原始后端字段，兼容现有代码
    id: String(raw.id ?? ''),
    title: safeParseString(raw.title),
    categoryId: raw.category_id || raw.categoryId ? String(raw.category_id || raw.categoryId) : undefined,
    category: safeParseString(raw.category_name || raw.category),
    publishDate: safeParseString(raw.publish_date || raw.created_at),
    source: safeParseString(raw.source),
    viewCount: Number(raw.view_count ?? raw.viewCount ?? 0),
    isTop: Boolean(raw.is_top ?? raw.isTop),
    content: safeParseString(raw.content),
  };
}

function parseDateTime(value: string) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function normalizeJobFairStatus(rawStatus: string, startDate: string, endDate: string): JobFair['status'] {
  const normalized = rawStatus.toLowerCase();
  if (normalized === 'ended' || normalized === 'ongoing') return normalized;
  if (normalized === 'upcoming' || normalized === 'registering' || normalized === 'active') return 'upcoming';
  const start = parseDateTime(startDate);
  const end = parseDateTime(endDate) || start;
  if (start && end) {
    const current = new Date();
    if (current >= end) return 'ended';
    if (current >= start && current < end) return 'ongoing';
    return 'upcoming';
  }
  return 'upcoming';
}

export function mapJobFair(raw: Record<string, unknown>): JobFair & Record<string, unknown> {
  const jobs = Array.isArray(raw.jobs) ? raw.jobs : [];
  const enterprises = Array.isArray(raw.enterprises) ? raw.enterprises : [];
  const enterpriseCount = safeParseNumber(raw.approved_registration_count ?? raw.approvedRegistrationCount ?? raw.enterprise_count ?? raw.enterpriseCount ?? raw.enterprise_num ?? raw.enterpriseNum);
  const jobCount = safeParseNumber(raw.job_count ?? raw.jobCount ?? raw.position_count ?? raw.positionCount);
  const startDate = safeParseString(raw.date || raw.start_date || raw.startDate || raw.start_time || raw.startTime);
  const endDate = safeParseString(raw.end_date || raw.endDate || raw.end_time || raw.endTime);
  const status = normalizeJobFairStatus(safeParseString(raw.status), startDate, endDate);

  return {
    ...raw, // 保留原始后端字段，兼容现有代码
    id: String(raw.id ?? ''),
    title: safeParseString(raw.title || raw.name),
    date: formatDateTime(startDate),
    endDate: formatDateTime(endDate),
    location: safeParseString(raw.location),
    status,
    enterpriseCount: enterpriseCount > 0 ? enterpriseCount : enterprises.length,
    jobCount: jobCount > 0 ? jobCount : jobs.length,
    headcount: safeParseNumber(raw.headcount ?? raw.head_count ?? raw.headCount),
    resumeCount: safeParseNumber(raw.resume_count ?? raw.resumeCount ?? raw.apply_count ?? raw.applyCount),
    description: safeParseString(raw.description),
    organizer: safeParseString(raw.organizer_name || raw.organizerName || raw.organizer || raw.org_name || raw.orgName),
  };
}
